// Admin version unlocks.
//
// Every version except the live one is locked (main.js). An admin can open a
// locked version back up — for one job or for all jobs — until the end of the
// day, from the top menu or Admin › Versions, with no deploy. The version locks
// again on its own when the unlock runs out, or when an admin locks it.
//
// Each unlock is one Forecast_User_Data row on the GLOBAL sentinel job at
// Version_GUID 0 (the AI prompt rows use the same sentinel), so version builds
// never copy it forward:
//   Forecast_Tab        'Version Unlock'
//   Forecast_Row_ID     '<Version_GUID>|<BU_ID or ALL>'
//   Forecast_Field_Name 'unlock'
//   Forecast_Value      JSON {until, by} — until null = locked again
import { loadUserDataRaw, updateUserDataArray } from './company';

export const VERSION_UNLOCK_TAB = 'Version Unlock';
export const ALL_JOBS = 'ALL';
const UNLOCK_BU_ID = 'GLOBAL';
// readUserData binds Version_GUID as sql.Int, so the sentinel must be numeric.
const UNLOCK_VERSION_GUID = '0';

const rowTime = (r) => Math.max(
    new Date(r.DSS_Updated_Datetime || 0).getTime() || 0,
    new Date(r.DSS_Created_Datetime || r.DSS_Created_Date || 0).getTime() || 0
);

/** Unlock rows → [{version, job, until, by}], latest row per version + job,
 *  dropping the ones that were locked again. */
export const parseVersionUnlocks = (rows) => {
    const latest = {};
    for (const r of rows || []) {
        if (r.Forecast_Tab !== VERSION_UNLOCK_TAB || r.Forecast_Field_Name !== 'unlock') continue;
        const key = String(r.Forecast_Row_ID || '');
        if (!latest[key] || rowTime(r) >= rowTime(latest[key])) latest[key] = r;
    }
    return Object.values(latest)
        .map((r) => {
            const [version, job] = String(r.Forecast_Row_ID || '').split('|');
            let v = {};
            try { v = JSON.parse(r.Forecast_Value || '{}') || {}; } catch (e) { v = {}; }
            return { version, job: job || ALL_JOBS, until: v.until || null, by: v.by || '' };
        })
        .filter((u) => u.version && u.until);
};

export const isUnlockActive = (u, now = Date.now()) =>
    !!u && !!u.until && new Date(u.until).getTime() > now;

/** The unlock that opens `version` for `job`, if any — the job's own unlock
 *  first, then an all-jobs one. Expired unlocks don't count. */
export const activeVersionUnlock = (unlocks, version, job, now = Date.now()) => {
    if (!version) return null;
    const open = (unlocks || []).filter(
        (u) => String(u.version) === String(version) && isUnlockActive(u, now)
    );
    return open.find((u) => job && u.job === String(job)) ||
        open.find((u) => u.job === ALL_JOBS) || null;
};

/** Unlocks still open on `version`, all-jobs first. */
export const openUnlocksFor = (unlocks, version, now = Date.now()) =>
    (unlocks || [])
        .filter((u) => String(u.version) === String(version) && isUnlockActive(u, now))
        .sort((a, b) => (a.job === ALL_JOBS ? -1 : b.job === ALL_JOBS ? 1 : a.job.localeCompare(b.job)));

export const endOfToday = (now = new Date()) => {
    const d = new Date(now);
    d.setHours(23, 59, 0, 0);
    return d;
};

export const loadVersionUnlocks = async () => {
    const res = await loadUserDataRaw({ id: UNLOCK_BU_ID, forecastGuid: UNLOCK_VERSION_GUID });
    return parseVersionUnlocks(res?.data);
};

/** Open `version` for `job` (or ALL_JOBS) until `until`; until null locks it again. */
export const saveVersionUnlock = ({ version, job, until, by }) =>
    updateUserDataArray({
        id: UNLOCK_BU_ID,
        version: UNLOCK_VERSION_GUID,
        tab: VERSION_UNLOCK_TAB,
        rowId: `${version}|${job || ALL_JOBS}`,
        fieldName: 'unlock',
        value: JSON.stringify({ until: until ? new Date(until).toISOString() : null, by: by || '' }),
        // Interpolated unconditionally by updateUserDataArray; keep them from
        // landing as the literal text 'undefined'.
        costType: '', costCode: '', vendor: '', po: '', equipment: '',
        // The unlock row lives on version 0, never behind a version lock.
        lockedVersion: false,
    });

/** The same change applied to a local unlock list, so the screen updates
 *  before the next read comes back. */
export const applyVersionUnlock = (unlocks, { version, job, until, by }) => [
    ...(unlocks || []).filter(
        (u) => !(String(u.version) === String(version) && u.job === (job || ALL_JOBS))
    ),
    ...(until ? [{ version: String(version), job: job || ALL_JOBS, until: new Date(until).toISOString(), by: by || '' }] : []),
];
